import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  Button,
  Input,
  Badge,
  Table,
  Spinner,
  Flex,
  Alert,
  NativeSelect,
  SimpleGrid,
} from '@chakra-ui/react';
import { userService } from '../services/userService';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [whitelistedEmails, setWhitelistedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [message, setMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  // Form state for creating JWT user
  const [newUser, setNewUser] = useState({
    email: '',
    displayName: '',
    password: '',
    role: 'Admin'
  });

  // Form state for adding whitelisted email
  const [newWhitelistEmail, setNewWhitelistEmail] = useState({
    email: '',
    notes: ''
  });

  useEffect(() => {
    loadUsers();
    loadWhitelistedEmails();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadWhitelistedEmails = async () => {
    try {
      const response = await userService.getWhitelistedEmails();
      setWhitelistedEmails(response.data);
    } catch (error) {
      console.error('Error loading whitelisted emails:', error);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    setUpdating(userId);
    setError(null);
    setMessage(null);

    try {
      await userService.updateUserRole(userId, newRole);
      await loadUsers();
      setMessage(`User role updated to ${newRole} successfully`);
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role');
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!newUser.email || !newUser.password) {
      setError('Email and password are required');
      return;
    }

    try {
      await userService.createUser({
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        displayName: newUser.displayName
      });

      setMessage(`User created successfully (${newUser.role})`);
      setNewUser({ email: '', displayName: '', password: '', role: 'Admin' });
      await loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleAddWhitelistEmail = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!newWhitelistEmail.email) {
      setError('Email is required');
      return;
    }

    try {
      await userService.addWhitelistedEmail(newWhitelistEmail);
      setMessage('Email added to whitelist successfully');
      setNewWhitelistEmail({ email: '', notes: '' });
      await loadWhitelistedEmails();
      await loadUsers();
    } catch (error) {
      console.error('Error adding whitelisted email:', error);
      setError(error.response?.data?.error || 'Failed to add email to whitelist');
    }
  };

  const handleRemoveWhitelistEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from whitelist?`)) {
      return;
    }

    setError(null);
    setMessage(null);

    try {
      await userService.removeWhitelistedEmail(email);
      setMessage('Email removed from whitelist successfully');
      await loadWhitelistedEmails();
      await loadUsers();
    } catch (error) {
      console.error('Error removing whitelisted email:', error);
      setError(error.response?.data?.error || 'Failed to remove email from whitelist');
    }
  };

  const toggleUserWhitelist = async (userId, currentStatus) => {
    setUpdating(userId);
    setError(null);
    setMessage(null);

    try {
      await userService.updateUserWhitelist(userId, !currentStatus);
      setMessage(`User whitelist status ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      await loadUsers();
      await loadWhitelistedEmails();
    } catch (error) {
      console.error('Error updating user whitelist status:', error);
      setError(error.response?.data?.error || 'Failed to update whitelist status');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';

    try {
      const utcDate = new Date(dateString);
      const vietnamTime = new Date(utcDate.getTime() + (7 * 60 * 60 * 1000));

      return vietnamTime.toLocaleString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin': return 'red';
      case 'Editor': return 'orange';
      default: return 'blue';
    }
  };

  // Pagination Logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(users.length / usersPerPage);

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.xl">
          <Flex justify="center" align="center" py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.500">Loading users...</Text>
            </VStack>
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center" py={4}>
            <Heading as="h1" size="2xl" color="purple.600" mb={3}>User Management</Heading>
            <Text fontSize="xl" color="gray.500">
              Manage user roles and whitelist access. Whitelisted users can login without being in any Microsoft group.
            </Text>
          </Box>

          {/* Messages */}
          {error && (
            <Alert.Root status="error" borderRadius="md">
              <Alert.Indicator />
              {error}
            </Alert.Root>
          )}

          {message && (
            <Alert.Root status="success" borderRadius="md">
              <Alert.Indicator />
              {message}
            </Alert.Root>
          )}

          {/* Create JWT User Card */}
          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="blue.500">
            <Card.Header>
              <Heading size="md" color="gray.700">Create User (JWT)</Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Create a local JWT user (use this for Admin/Editor accounts).
              </Text>
            </Card.Header>
            <Card.Body>
              <form onSubmit={handleCreateUser}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} rowGap={6} mb={6}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Email</Text>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="admin@example.com"
                      required
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Display Name (optional)</Text>
                    <Input
                      value={newUser.displayName}
                      onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                      placeholder="Admin"
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Password</Text>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Set a strong password"
                      required
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Role</Text>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={newUser.role}
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      >
                        <option value="Admin">Admin</option>
                        <option value="Editor">Editor</option>
                        <option value="Editor Branch">Editor Branch</option>
                        <option value="User">User</option>
                        <option value="User Branch">User Branch</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Box>
                </SimpleGrid>

                <Button type="submit" colorPalette="blue">
                  Create User
                </Button>
              </form>
            </Card.Body>
          </Card.Root>

          {/* Users Table Card */}
          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="purple.500">
            <Card.Header>
              <Heading size="md" color="gray.700">Registered Users</Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                {users.length} user{users.length !== 1 ? 's' : ''} registered
              </Text>
            </Card.Header>
            <Card.Body>
              {users.length === 0 ? (
                <Alert.Root status="info" borderRadius="md">
                  <Alert.Indicator />
                  No users found.
                </Alert.Root>
              ) : (
                <>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Email</Table.ColumnHeader>
                        <Table.ColumnHeader>Display Name</Table.ColumnHeader>
                        <Table.ColumnHeader>Current Role</Table.ColumnHeader>
                        <Table.ColumnHeader>Whitelisted</Table.ColumnHeader>
                        <Table.ColumnHeader>Last Login</Table.ColumnHeader>
                        <Table.ColumnHeader>Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {currentUsers.map((user) => (
                        <Table.Row key={user.id}>
                          <Table.Cell fontFamily="mono" fontSize="sm">{user.email}</Table.Cell>
                          <Table.Cell>{user.display_name || '-'}</Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={getRoleBadgeColor(user.role)}>
                              {user.role || 'Not assigned'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <VStack align="start" gap={1}>
                              <Button
                                size="xs"
                                colorPalette={user.is_effective_whitelisted ? 'green' : 'gray'}
                                variant={user.is_effective_whitelisted ? 'solid' : 'outline'}
                                onClick={() => toggleUserWhitelist(user.id, user.is_email_whitelisted)}
                                disabled={updating === user.id}
                              >
                                {user.is_effective_whitelisted ? '✓ Yes' : '✗ No'}
                              </Button>
                              <HStack gap={2} wrap="wrap">
                                {user.is_email_whitelisted && (
                                  <Badge size="xs" colorPalette="green" variant="subtle">Email</Badge>
                                )}
                                {user.is_group_whitelisted && (
                                  <Badge size="xs" colorPalette="purple" variant="subtle">Group</Badge>
                                )}
                                {!user.is_email_whitelisted && !user.is_group_whitelisted && (
                                  <Badge size="xs" colorPalette="gray" variant="subtle">None</Badge>
                                )}
                              </HStack>
                            </VStack>
                          </Table.Cell>
                          <Table.Cell fontSize="sm">{formatDate(user.last_login)}</Table.Cell>
                          <Table.Cell>
                            <HStack>
                              <NativeSelect.Root size="sm" width="100px">
                                <NativeSelect.Field
                                  value={user.role || 'User'}
                                  onChange={(e) => updateUserRole(user.id, e.target.value)}
                                  disabled={updating === user.id}
                                >
                                  <option value="User">User</option>
                                  <option value="User Branch">User Branch</option>
                                  <option value="Editor">Editor</option>
                                  <option value="Editor Branch">Editor Branch</option>
                                  <option value="Admin">Admin</option>
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                              {updating === user.id && (
                                <Spinner size="sm" color="purple.500" />
                              )}
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Flex justify="center" align="center" mt={6} gap={4}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Text fontWeight="bold">
                        Page {currentPage} of {totalPages}
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </Flex>
                  )}
                </>
              )}
            </Card.Body>
          </Card.Root>

          {/* Email Whitelist Management Card */}
          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="green.500">
            <Card.Header>
              <Heading size="md" color="gray.700">Email Whitelist Management</Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Add individual users by email to allow access without requiring group membership.
              </Text>
            </Card.Header>
            <Card.Body>
              {/* Add Email Form */}
              <form onSubmit={handleAddWhitelistEmail}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} rowGap={6} mb={6}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Email Address</Text>
                    <Input
                      type="email"
                      value={newWhitelistEmail.email}
                      onChange={(e) => setNewWhitelistEmail({ ...newWhitelistEmail, email: e.target.value })}
                      placeholder="user@example.com"
                      required
                    />
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Notes (optional)</Text>
                    <Input
                      value={newWhitelistEmail.notes}
                      onChange={(e) => setNewWhitelistEmail({ ...newWhitelistEmail, notes: e.target.value })}
                      placeholder="Reason for whitelisting..."
                    />
                  </Box>
                </SimpleGrid>
                <Button type="submit" colorPalette="green">Add to Whitelist</Button>
              </form>

              {/* Whitelisted Emails Table */}
              <Heading size="sm" mt={8} mb={4}>Whitelisted Emails</Heading>
              {whitelistedEmails.length === 0 ? (
                <Alert.Root status="info" borderRadius="md">
                  <Alert.Indicator />
                  No emails whitelisted yet. Add emails above to allow individual access.
                </Alert.Root>
              ) : (
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Email</Table.ColumnHeader>
                      <Table.ColumnHeader>Notes</Table.ColumnHeader>
                      <Table.ColumnHeader>Added By</Table.ColumnHeader>
                      <Table.ColumnHeader>Added At</Table.ColumnHeader>
                      <Table.ColumnHeader>Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {whitelistedEmails.map((item) => (
                      <Table.Row key={item.id}>
                        <Table.Cell fontFamily="mono" fontSize="sm">{item.email}</Table.Cell>
                        <Table.Cell>{item.notes || '-'}</Table.Cell>
                        <Table.Cell>{item.added_by || '-'}</Table.Cell>
                        <Table.Cell fontSize="sm">{formatDate(item.added_at)}</Table.Cell>
                        <Table.Cell>
                          <Button
                            size="xs"
                            colorPalette="red"
                            variant="solid"
                            onClick={() => handleRemoveWhitelistEmail(item.email)}
                          >
                            Remove
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              )}
            </Card.Body>
          </Card.Root>

          {/* Role Descriptions Card */}
          <Card.Root shadow="lg" borderRadius="xl">
            <Card.Header>
              <Heading size="md" color="gray.700">Role Descriptions</Heading>
            </Card.Header>
            <Card.Body>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Badge colorPalette="red" px={2}>Admin</Badge>
                  <Text>Full system access, can manage users, groups, upload files, and view all data</Text>
                </HStack>
                <HStack>
                  <Badge colorPalette="orange" px={2}>Editor</Badge>
                  <Text>Can upload files and view all data, but cannot manage users or groups</Text>
                </HStack>
                <HStack>
                  <Badge colorPalette="blue" px={2}>User</Badge>
                  <Text>Can only search and view data, cannot upload or manage</Text>
                </HStack>
              </VStack>
              <Text fontSize="sm" color="gray.500" mt={4} fontStyle="italic">
                Note: Whitelisted users can login without being in any Microsoft group. Roles are assigned individually per user.
              </Text>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default UserManager;
